import { bridge } from '../bridge'

// X-Hearth-Client wiring for the version handshake.
// Spec: docs/forward-compat-version-handshake.md.
//
// On every authenticated HTTP call we send `X-Hearth-Client: <kind>/<ver>`.
// The WS dial URL gets the same values as `client_kind` + `client_version`
// query params. Values come from the native shell via `app.info` and are
// cached for the lifetime of the page.

type ClientHeader = { kind: 'ios' | 'android' | 'web'; version: string }

let cached: ClientHeader | null = null
let pending: Promise<ClientHeader> | null = null

async function load(): Promise<ClientHeader> {
  try {
    const info = await bridge.call('app.info')
    // In a native shell, version is the native build's
    // CFBundleShortVersionString / versionName. In the production web
    // bridge (createWebBridge) it's the git short-sha injected via
    // VITE_GIT_SHA. Either way it's what HEARTH_MIN_<KIND> gates on.
    return { kind: info.platform, version: info.version || '0.0.0' }
  } catch {
    // Defensive last-ditch fallback if the bridge call literally throws
    // (shouldn't happen — every transport implements app.info). Reports
    // as `web` so a future HEARTH_MIN_WEB policy gates it.
    return { kind: 'web', version: '0.0.0-unknown' }
  }
}

export async function getClientHeader(): Promise<ClientHeader> {
  if (cached) return cached
  if (!pending) {
    pending = load().then((h) => {
      cached = h
      return h
    })
  }
  return pending
}

export async function clientHeaderValue(): Promise<string> {
  const h = await getClientHeader()
  return `${h.kind}/${h.version}`
}
